import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DollarSign, Filter, FileSpreadsheet, Loader2, Plus, AlertTriangle, CheckCircle2, Wallet } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';

const fmtDate = (d) => d.toISOString().slice(0, 10);

const AdminCommissionsTab = () => {
  const { getToken } = useAuth();
  const [report, setReport] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => fmtDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo, setDateTo] = useState(() => fmtDate(new Date()));
  const [reportStatus, setReportStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [owed, setOwed] = useState(null);

  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === 'this_month') {
      setDateFrom(fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)));
      setDateTo(fmtDate(now));
    } else if (preset === 'last_month') {
      setDateFrom(fmtDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
      setDateTo(fmtDate(new Date(now.getFullYear(), now.getMonth(), 0)));
    } else if (preset === 'last_30') {
      setDateFrom(fmtDate(new Date(now.getTime() - 30 * 86400000)));
      setDateTo(fmtDate(now));
    } else if (preset === 'all') {
      setDateFrom('');
      setDateTo('');
    }
  };

  // Manual commission modal state
  const [showManualModal, setShowManualModal] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [mcAffiliateId, setMcAffiliateId] = useState('');
  const [mcCompanyId, setMcCompanyId] = useState('');
  const [mcAmount, setMcAmount] = useState('');
  const [mcReason, setMcReason] = useState('Vinculo retroativo — cliente pagou antes da associacao');
  const [mcSaving, setMcSaving] = useState(false);
  const [companySearch, setCompanySearch] = useState('');

  const openManualModal = async () => {
    setShowManualModal(true);
    try {
      const token = getToken();
      const [affRes, compRes] = await Promise.all([
        axios.get(`${API}/admin/affiliates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/companies`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAffiliates((affRes.data || []).filter(a => a.status === 'approved'));
      setCompanies(compRes.data || []);
    } catch {
      toast.error('Erro ao carregar afiliados ou empresas');
    }
  };

  const submitManualCommission = async () => {
    if (!mcAffiliateId || !mcCompanyId || !mcAmount) {
      toast.error('Preencha afiliado, empresa e valor');
      return;
    }
    setMcSaving(true);
    try {
      const token = getToken();
      const res = await axios.post(
        `${API}/admin/commissions/manual`,
        {
          affiliate_id: mcAffiliateId,
          company_id: mcCompanyId,
          amount: parseFloat(mcAmount),
          reason: mcReason.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Comissao criada!');
      setShowManualModal(false);
      setMcAffiliateId(''); setMcCompanyId(''); setMcAmount(''); setCompanySearch('');
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar comissao manual');
    } finally {
      setMcSaving(false);
    }
  };

  const filteredCompanies = companySearch.trim()
    ? companies.filter(c => (c.name || '').toLowerCase().includes(companySearch.toLowerCase().trim()))
    : companies.slice(0, 50);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (reportStatus !== 'all') params.append('status', reportStatus);
      const res = await axios.get(`${API}/admin/commission-report?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setReport(res.data);
    } catch { toast.error('Erro ao carregar relatorio'); }
    finally { setLoading(false); }
  }, [getToken, dateFrom, dateTo, reportStatus]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  useEffect(() => {
    const token = getToken();
    axios.get(`${API}/admin/commissions/owed`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setOwed(r.data)).catch(() => {});
  }, [getToken]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (reportStatus !== 'all') params.append('status', reportStatus);
      const res = await axios.get(`${API}/admin/commission-report/export?${params}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `comissoes_${dateFrom || 'inicio'}_a_${dateTo || 'hoje'}_${reportStatus}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Planilha exportada!');
    } catch { toast.error('Erro ao exportar'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="comissoes-section">
      {/* ═════ Painel: Devo para alguém? ═════ */}
      {owed && (
        owed.affiliates_owed.length === 0 ? (
          <Card className="border-0 shadow-sm bg-emerald-50 border-l-4 border-l-emerald-500" data-testid="owed-panel-empty">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-emerald-800 text-sm">Ninguém a pagar</p>
                <p className="text-xs text-emerald-700">Todas as comissões de afiliados estão quitadas.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-500" data-testid="owed-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Devo para {owed.affiliates_owed.length} afiliado(s)
                <span className="ml-auto text-lg font-extrabold text-amber-600" data-testid="owed-total">R$ {owed.total_owed.toFixed(2)}</span>
              </CardTitle>
              <CardDescription>Total pendente por pessoa, independente do período do relatório</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1.5">
                {owed.affiliates_owed.map(a => (
                  <div key={a.affiliate_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 p-2.5 bg-amber-50/60 rounded-lg border border-amber-100" data-testid={`owed-row-${a.affiliate_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 text-sm truncate">{a.name}</p>
                      <p className="text-[10px] text-slate-500 font-mono truncate">{a.pix_key ? `PIX: ${a.pix_key}` : '⚠️ Sem chave PIX cadastrada'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-extrabold text-amber-700 text-sm">R$ {a.total.toFixed(2)}</p>
                      <p className="text-[10px] text-slate-500">{a.count} comissão(ões) · desde {a.oldest?.slice(0, 10)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">Pague com 1 clique na aba Afiliados (Pix automático) ou exporte o Excel abaixo para lote.</p>
            </CardContent>
          </Card>
        )
      )}

      {/* ═════ Alerta: Parceiros com split inativo ═════ */}
      {owed && owed.partners_split_inactive.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-red-500" data-testid="partners-split-alert">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="w-5 h-5 text-red-500" />
              {owed.partners_split_inactive.length} parceiro(s) com clientes pagantes e split INATIVO
            </CardTitle>
            <CardDescription>Sem a Wallet ID do Asaas configurada, o repasse automático não acontece — você pode estar acumulando dívida com eles</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1.5">
              {owed.partners_split_inactive.map(p => (
                <div key={p.partner_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 p-2.5 bg-red-50/60 rounded-lg border border-red-100" data-testid={`split-alert-row-${p.partner_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 text-sm truncate">{p.name}</p>
                    <p className="text-[10px] text-slate-500">{p.clients} cliente(s) pagante(s) · comissão {p.commission_rate}%</p>
                  </div>
                  <p className="font-extrabold text-red-600 text-sm">~R$ {p.est_monthly.toFixed(2)}/mês</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Configure a Wallet ID na aba Parceiros para o Asaas repassar automaticamente.</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm border-l-4 border-l-[#C5A059]">
        <CardContent className="p-5">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[['this_month', 'Este mês'], ['last_month', 'Mês passado'], ['last_30', 'Últimos 30 dias'], ['all', 'Tudo']].map(([id, label]) => (
              <button key={id} onClick={() => applyPreset(id)} data-testid={`preset-${id}`}
                className="px-3 py-1 rounded-full text-xs font-semibold border border-slate-200 bg-white text-slate-600 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">De</label><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40" data-testid="report-date-from" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Até</label><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40" data-testid="report-date-to" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Status</label><select value={reportStatus} onChange={(e) => setReportStatus(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="report-status-filter"><option value="all">Todos</option><option value="pending">Pendentes</option><option value="paid">Pagos</option></select></div>
            <Button onClick={fetchReport} className="bg-[#0A2647] hover:bg-[#0A2647]/90 text-white h-9 px-4 text-sm" data-testid="report-filter-btn"><Filter className="w-3.5 h-3.5 mr-1.5" />Filtrar</Button>
            <Button onClick={openManualModal} className="bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-bold h-9 px-4 text-sm" data-testid="manual-commission-btn"><Plus className="w-3.5 h-3.5 mr-1.5" />Comissao Manual</Button>
            <Button onClick={exportExcel} disabled={exporting || !report?.commissions?.length} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4 text-sm ml-auto" data-testid="report-export-btn">{exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}Exportar Excel</Button>
          </div>
        </CardContent>
      </Card>

      {report && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-slate-900" data-testid="report-total-count">{report.summary.total}</p><p className="text-xs text-slate-500">Total Comissoes</p></CardContent></Card>
          <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-slate-900" data-testid="report-total-general">R$ {report.summary.total_general.toFixed(2)}</p><p className="text-xs text-slate-500">Valor Total</p></CardContent></Card>
          <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600" data-testid="report-total-pending">R$ {report.summary.total_pending.toFixed(2)}</p><p className="text-xs text-slate-500">Pendente</p></CardContent></Card>
          <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600" data-testid="report-total-paid">R$ {report.summary.total_paid.toFixed(2)}</p><p className="text-xs text-slate-500">Pago</p></CardContent></Card>
        </div>
      )}

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#C5A059]" />Relatorio de Comissoes</CardTitle><CardDescription>Visualize e exporte comissoes para facilitar pagamentos em lote via PIX</CardDescription></CardHeader>
        <CardContent>
          {loading ? <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 border-4 border-[#C5A059] border-t-transparent rounded-full" /></div>
          : !report?.commissions?.length ? <p className="text-sm text-slate-400 text-center py-8">Nenhuma comissao encontrada para o período selecionado.</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead><tr className="border-b text-slate-500 text-xs"><th className="text-left py-2 px-3">Afiliado</th><th className="text-left py-2 px-3">Chave PIX</th><th className="text-left py-2 px-3">Empresa</th><th className="text-center py-2 px-3">Plano</th><th className="text-center py-2 px-3">Valor</th><th className="text-center py-2 px-3">Status</th><th className="text-center py-2 px-3">Data</th></tr></thead>
                <tbody>
                  {report.commissions.map(c => {
                    const inst = c.installment_1 || {};
                    const isBonus = c.is_master_bonus;
                    return (
                      <tr key={c.id} className={`border-b border-slate-100 ${isBonus ? 'bg-amber-50/40' : ''}`} data-testid={`report-row-${c.id}`}>
                        <td className="py-2.5 px-3"><p className="font-medium text-slate-900">{c.affiliate_name}</p><p className="text-[10px] text-slate-400">{isBonus ? 'Bonus Master' : c.affiliate_type === 'master' ? 'Master' : 'Padrao'}</p></td>
                        <td className="py-2.5 px-3 text-xs text-slate-600 font-mono">{c.affiliate_pix || '-'}</td>
                        <td className="py-2.5 px-3"><p className="text-slate-800">{c.company_name}</p>{isBonus && c.origin_affiliate_name && <p className="text-[10px] text-amber-600">via {c.origin_affiliate_name}</p>}</td>
                        <td className="text-center py-2.5 px-3 text-xs text-slate-600">{c.plan_name}</td>
                        <td className="text-center py-2.5 px-3 font-bold text-emerald-600">R$ {(inst.amount || 0).toFixed(2)}</td>
                        <td className="text-center py-2.5 px-3" data-testid={`report-status-${c.id}`}><span className={`text-xs font-semibold px-2 py-1 rounded-full ${inst.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : inst.status === 'awaiting_client_payment' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>{inst.status === 'paid' ? 'Pago' : inst.status === 'awaiting_client_payment' ? 'Aguardando pagamento do cliente' : 'A pagar'}</span></td>
                        <td className="text-center py-2.5 px-3 text-xs text-slate-500">{c.created_at?.slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═════ Modal: Comissão Manual ═════ */}
      <Dialog open={showManualModal} onOpenChange={setShowManualModal}>
        <DialogContent className="sm:max-w-lg" data-testid="manual-commission-modal">
          <DialogHeader>
            <DialogTitle>Nova Comissao Manual</DialogTitle>
            <DialogDescription>
              Use para registrar comissoes de clientes vinculados ao afiliado DEPOIS do primeiro pagamento.
              A comissao entra como <strong>pendente</strong> e pode ser paga via Pix automatico na aba Afiliados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium">Afiliado</Label>
              <select
                value={mcAffiliateId}
                onChange={(e) => setMcAffiliateId(e.target.value)}
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white mt-1"
                data-testid="mc-affiliate-select"
              >
                <option value="">-- Selecione o afiliado --</option>
                {affiliates.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-medium">Buscar Empresa (por nome)</Label>
              <Input
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                placeholder="Ex: Santa Helena"
                className="h-10 mt-1"
                data-testid="mc-company-search"
              />
              <select
                value={mcCompanyId}
                onChange={(e) => setMcCompanyId(e.target.value)}
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white mt-1"
                data-testid="mc-company-select"
              >
                <option value="">-- Selecione a empresa --</option>
                {filteredCompanies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {c.plan || 'free'}
                  </option>
                ))}
              </select>
              {!companySearch && companies.length > 50 && (
                <p className="text-[10px] text-slate-400 mt-1">Digite para buscar entre {companies.length} empresas</p>
              )}
            </div>
            <div>
              <Label className="text-xs font-medium">Valor (R$)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={mcAmount}
                onChange={(e) => setMcAmount(e.target.value)}
                placeholder="100.00"
                className="h-10 mt-1"
                data-testid="mc-amount"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Motivo (historico)</Label>
              <Input
                value={mcReason}
                onChange={(e) => setMcReason(e.target.value)}
                className="h-10 mt-1"
                data-testid="mc-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualModal(false)} disabled={mcSaving} data-testid="mc-cancel-btn">Cancelar</Button>
            <Button
              onClick={submitManualCommission}
              disabled={mcSaving || !mcAffiliateId || !mcCompanyId || !mcAmount}
              className="bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-bold"
              data-testid="mc-submit-btn"
            >
              {mcSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Registrar Comissao
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommissionsTab;
